'use client';

import Conditional from '@next-app/components/Conditional';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import {
  selectDynSess,
  selectIsFurniture,
  selectIsGuestUser,
  selectIsLoggedIn,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import {
  selectBillingAddress,
  selectCommerceItems,
  selectDisablePaymentList,
  selectEmailInfo,
  selectGiftingItemFrom,
  selectHasItemFromRegistry,
  selectHasItemFromShippingList,
  selectIsBopisOrder,
  selectIsDigitalOrder,
  selectIsGiftCertificateOverTotal,
  selectIsGiftItemAnonymous,
  selectIsGooglePayAuthorized,
  selectIsPaymentMethodStep,
  selectIsPayPalPaymentMethodAuthorized,
  selectIsShipOrder,
  selectIsSubmitOrder,
  selectOrderComments,
  selectOrderSummaryInfo,
  selectPaymentDetailInfo,
  selectPaymentMethodType,
  selectPaymentsInfo,
  selectSameAsBillingAddress,
  selectStoreDetails,
  selectTaxExemptOpted,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import {
  fetchCheckoutDetails,
  getCartItemsForDTMEvent,
  handleAPIDownErrors,
  submitOrderReq,
  triggerCheckoutFormErrorEvent,
  validateFormDetails,
} from '@next-app/utils/CheckoutHelper';
import { setInSessionStorage } from '@next-app/utils/session-storage-utils';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../ExpressCheckout/ExpressCheckout.module.scss';
import dynamic from 'next/dynamic';
import SkeletonUI from '@next-app/components/Skeleton/Skeleton';
import { PAYMENT } from '@next-app/constants/CheckoutConstants';
import { getUnformattedNumber } from '@next-app/utils/form-validation';
import {
  FormException,
  ISubmitOrderRequest,
  ISubmitOrderResponse,
} from '@next-app/lib/features/checkout/Checkout.interface';
import { useEffect, useState } from 'react';
import {
  setCreditCardFormError,
  setIsSubmitOrder,
  setBillingAddressError,
  setSubmitOrderError,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import Loader from '@next-app/components/shared/Loader/Loader';
import { CreditCardFormFields } from '../CCCardDetailsForm/CreditCardFormFields';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import { BillingAddressFormFields } from '../BillingAddressForm/BillingAddressFormFields';
import { getFurnitureTabUrl } from '@next-app/utils/common-utils';

// Lazy load PayPalButton for better performance
const PayPalButton = dynamic(
  () => import('@next-app/components/PDP/AddedToCartModal/PayPalButton'),
  {
    ssr: true,
    loading: () => <SkeletonUI width={'100%'} height={20} />,
  },
);

/**
 * SubmitOrder Component
 *
 * Handles the final order submission in the checkout process.
 * Manages:
 * - Different payment methods (Credit Card, PayPal, On Account)
 * - Order submission logic
 * - Loading states
 *
 * @returns {JSX.Element} The order submission section of the checkout page
 */
const SubmitOrder = () => {
  const dispatch = useDispatch();

  // Redux selectors with proper null checks
  const paymentStep = useSelector(selectIsPaymentMethodStep);
  const dynSessConf = useSelector(selectDynSess);
  const isRegistryOrder = useSelector(selectHasItemFromRegistry);
  const isCTLOrder = useSelector(selectHasItemFromShippingList);
  const giftingItemFrom = useSelector(selectGiftingItemFrom);
  const isGiftItemAnonymous = useSelector(selectIsGiftItemAnonymous);
  const orderSummaryInfo = useSelector(selectOrderSummaryInfo);
  const isSubmitOrder = useSelector(selectIsSubmitOrder);
  const paymentMethod = useSelector(selectPaymentMethodType);
  const billingAddressData = useSelector(selectBillingAddress);
  const isLoggedInUser = useSelector(selectIsLoggedIn);
  const sameAsDeliveryAddress = useSelector(selectSameAsBillingAddress);
  const disablePaymentList = useSelector(selectDisablePaymentList);
  const orderComments = useSelector(selectOrderComments) || '';
  const isPayPalAuthorized = useSelector(selectIsPayPalPaymentMethodAuthorized);
  const isGooglePayAuthorized = useSelector(selectIsGooglePayAuthorized);
  const isGiftCertificateOverTotal = useSelector(
    selectIsGiftCertificateOverTotal,
  );
  const isBopisOrder = useSelector(selectIsBopisOrder);
  const storeDetails = useSelector(selectStoreDetails);
  const commerceItems = useSelector(selectCommerceItems);
  const isGuestUser = useSelector(selectIsGuestUser);
  const emailInfo = useSelector(selectEmailInfo);
  const isTaxExemptOpted = useSelector(selectTaxExemptOpted);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);
  const isFurniture = useSelector(selectIsFurniture);
  const { mainPaymentType = '' } = useSelector(selectPaymentsInfo) || {};
  const isDigitalOrder = useSelector(selectIsDigitalOrder);
  const isShipOrder = useSelector(selectIsShipOrder);
  const { saveNewCreditCard = true } =
    useSelector(selectPaymentDetailInfo) || {};

  const { creditCardFormData, creditCardFormErrors = {} } =
    billingAddressData || {};

  // Local state
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Validates form details whenever dependencies change
   */
  useEffect(() => {
    if (
      !disablePaymentList &&
      ((paymentMethod === PAYMENT.NEW_CREDIT_CARD &&
        (!isLoggedInUser || !saveNewCreditCard)) ||
        paymentMethod === PAYMENT.ON_ACCOUNT)
    ) {
      const poaFormData = {
        companyName: billingAddressData?.billingAddressFormData?.companyName,
      };
      const poaFormError = {
        companyName:
          billingAddressData?.billingAddressForErrors?.companyName || '',
      };

      const ccForm = {
        creditCardNumber: creditCardFormData?.creditCardNumber || '',
        expirationMonth: creditCardFormData?.expirationMonth || '',
        expirationYear: creditCardFormData?.expirationYear || '',
        cardVerificationNumber:
          creditCardFormData?.cardVerificationNumber || '',
      };

      const ccFormError = {
        creditCardNumber: creditCardFormErrors?.creditCardNumber || '',
        expirationError: creditCardFormErrors?.expirationError || '',
        cardVerificationNumber:
          creditCardFormErrors?.cardVerificationNumber || '',
      };
      const {
        firstName = '',
        lastName = '',
        address1 = '',
        city = '',
        country = '',
        phoneNumber = '',
        postalCode = '',
        state = '',
      } = billingAddressData?.billingAddressFormData || {};
      const {
        firstName: firstNameError = '',
        lastName: lastNameError = '',
        address1: address1Error = '',
        city: cityError = '',
        country: countryError = '',
        phoneNumber: phoneNumberError = '',
        postalCode: postalCodeError = '',
        state: stateError = '',
      } = billingAddressData?.billingAddressForErrors || {};
      const billingAddressForm = {
        firstName,
        lastName,
        address1,
        city,
        country,
        phoneNumber,
        postalCode,
        state,
      };
      const billingAddressFormError = {
        firstName: firstNameError,
        lastName: lastNameError,
        address1: address1Error,
        city: cityError,
        country: countryError,
        phoneNumber: phoneNumberError,
        postalCode: postalCodeError,
        state: stateError,
      };
      const resp = validateFormDetails({
        isSameAsDelivery: sameAsDeliveryAddress,
        formData: paymentMethod === PAYMENT.ON_ACCOUNT ? poaFormData : ccForm,
        formError:
          paymentMethod === PAYMENT.ON_ACCOUNT ? poaFormError : ccFormError,
        billingAddressForm: billingAddressForm,
        billingAddressFormError: billingAddressFormError,
      });
      dispatch(setIsSubmitOrder(resp));
    }
    if (isPayPalAuthorized) {
      dispatch(setIsSubmitOrder(isPayPalAuthorized));
    }
    if (isGooglePayAuthorized) {
      dispatch(setIsSubmitOrder(isGooglePayAuthorized));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingAddressData,
    paymentMethod,
    sameAsDeliveryAddress,
    disablePaymentList,
    isGooglePayAuthorized,
  ]);

  /**
   * Handles order submission for non-PayPal payment methods
   */
  const handleOrderSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      let paymentType = '';
      const {
        firstName,
        lastName,
        address1,
        address2,
        companyName,
        attention,
        city,
        country,
        phoneNumber,
        postalCode,
        state,
        avsStatus,
      } = billingAddressData.billingAddressFormData;

      // eslint-disable-next-line no-unused-vars
      const { defaultPayment, ownerId, repositoryId, ...creditCardFormData } =
        billingAddressData?.creditCardFormData || {};

      let cardData =
        isLoggedInUser && !saveNewCreditCard
          ? creditCardFormData
          : billingAddressData?.creditCardFormData;

      // Base order request
      let orderSubmitRequest: ISubmitOrderRequest = {
        instructions: orderComments,
        customOrderDropdownValue: orderSummaryInfo.customOrderDropdownValue,
        providerNumber: orderSummaryInfo.providerNumber,
        siteNumber: orderSummaryInfo.siteNumber,
        giftItemFrom: giftingItemFrom,
        _dynSessConf: dynSessConf,
      };

      // Add credit card specific data if needed
      if (
        (paymentMethod === PAYMENT.NEW_CREDIT_CARD ||
          paymentMethod === PAYMENT.EXISTING_CREDIT_CARD) &&
        !isPayPalAuthorized &&
        !isGooglePayAuthorized &&
        (!isLoggedInUser ||
          (isLoggedInUser &&
            ((isDigitalOrder && !isShipOrder) || !saveNewCreditCard))) &&
        !isGiftCertificateOverTotal
      ) {
        paymentType = 'creditCard';
        orderSubmitRequest = {
          ...orderSubmitRequest,
          ...cardData,
          billingAddress: {
            firstName,
            lastName,
            address1,
            address2,
            companyName,
            attention,
            city,
            state,
            postalCode,
            country,
            phoneNumber: getUnformattedNumber(phoneNumber),
          },
        };
      }

      // Add POA specific data if needed
      if (
        (paymentMethod === PAYMENT.ON_ACCOUNT ||
          paymentMethod === PAYMENT.ON_ACCOUNT_CARD) &&
        !isPayPalAuthorized &&
        !isGooglePayAuthorized
      ) {
        paymentType = 'payOnAccount';
        orderSubmitRequest = {
          ...orderSubmitRequest,
          poNumber: billingAddressData?.poaFormData?.poNumber || '',
          invoiceReqPONumber: '',
          billingAddress: {
            firstName,
            lastName,
            address1,
            companyName,
            city,
            state,
            postalCode,
            country,
            phoneNumber: getUnformattedNumber(phoneNumber),
          },
          poaCompanyName: companyName,
        };
      }

      const submitOrderResponse = await submitOrderReq(
        orderSubmitRequest,
        isFurniture,
      );

      if (
        submitOrderResponse?.formError === true &&
        'formExceptions' in submitOrderResponse
      ) {
        triggerCheckoutFormErrorEvent({
          step: 'submitOrder',
          llmAPIVersion,
          isFurniture,
          errors: submitOrderResponse?.formExceptions,
        });
        handleSubmitOrderErrors(submitOrderResponse?.formExceptions);
        handlePaymentContinueDTMEvent({ paymentType, success: false });
        const error = handleAPIDownErrors(submitOrderResponse?.formExceptions);
        if (error) {
          dispatch(setSubmitOrderError(error));
        }
      }

      if (
        submitOrderResponse?.status === 'OK' &&
        submitOrderResponse?.formError === false &&
        submitOrderResponse?.redirectURL
      ) {
        setInSessionStorage(
          'llOrderConfirmID',
          JSON.parse(submitOrderResponse.details.orderId),
        );
        handlePaymentContinueDTMEvent({ paymentType, success: true });
        await handleOrderConfirmationData(submitOrderResponse);
        window.location.href = isFurniture
          ? getFurnitureTabUrl(submitOrderResponse?.redirectURL)
          : submitOrderResponse?.redirectURL;
      }
    } catch (error) {
      console.error('Order submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles errors in submit order API response
   */
  const handleSubmitOrderErrors = (formErrors: FormException[]) => {
    let fieldErrors: Record<string, string> = {};
    formErrors.forEach(({ propertyPath, localizedMessage }: FormException) => {
      fieldErrors[propertyPath] = localizedMessage;
    });

    // Handle credit card form errors
    const ccFormFields = Object.values(CreditCardFormFields) as string[];
    const ccFormErrors = Object.fromEntries(
      Object.entries(fieldErrors).filter(([key]) => ccFormFields.includes(key)),
    );
    if (ccFormErrors) {
      dispatch(setCreditCardFormError(ccFormErrors));
    }

    // Handle billing address form errors
    const billingAddressFields = [
      BillingAddressFormFields.FIRST_NAME,
      BillingAddressFormFields.LAST_NAME,
      BillingAddressFormFields.COUNTRY,
      BillingAddressFormFields.CITY,
      BillingAddressFormFields.STATE,
      BillingAddressFormFields.POSTAL_CODE,
      BillingAddressFormFields.ADDRESS_ONE,
      BillingAddressFormFields.PHONE_NO,
    ];
    const billingAddressErrors = Object.fromEntries(
      Object.entries(fieldErrors).filter(([key]) =>
        billingAddressFields.includes(key as BillingAddressFormFields),
      ),
    );
    if (billingAddressErrors) {
      dispatch(
        setBillingAddressError({
          ...billingAddressErrors,
        }),
      );
    }
  };

  /**
   * Handles DTM event for payment continue
   */
  const handlePaymentContinueDTMEvent = ({
    paymentType,
    success,
  }: {
    paymentType: string;
    success: boolean;
  }) => {
    const eventData = {
      cart: {
        items: getCartItemsForDTMEvent(commerceItems),
      },
      isFurniture: isFurniture,
      llmAPIVersion: llmAPIVersion,
      paymentType: paymentType || mainPaymentType,
      success: success,
      taxExempt: isTaxExemptOpted,
    };
    DynamicDTMEvent({ event: 'paymentContinue', detail: eventData });
    return;
  };

  /**
   * Handles event data on submit order success for order confirmation page.
   */
  const handleOrderConfirmationData = async (data: ISubmitOrderResponse) => {
    const orderData = data?.confirmationMessage?.order;
    const eventData = {
      couponCode: orderData?.couponCode || '',
      discount: orderData?.priceInfo?.discountAmount,
      email: emailInfo.email,
      emailOptIn: (isGuestUser && emailInfo?.emailOptIn) || false,
      fromPage: 'checkout',
      items: commerceItems.map((item) => ({
        ageRange: item?.gaEventData?.ageRange,
        discounted: item?.priceInfo?.discounted,
        extendedPrice: item?.priceInfo?.amount,
        gradeRange: item?.gaEventData?.gradeRange,
        item: item?.catalogRefId,
        inStorePickup: item?.instorePickUpOpted || false,
        listPrice: item?.priceInfo?.listPrice,
        onSale: item?.priceInfo?.onSale,
        orderDiscountShare: item?.priceInfo?.orderDiscountShare,
        price: parseFloat(
          ((item?.priceInfo?.amount || 0) / (item?.quantity || 1))?.toFixed(2),
        ),
        primarySubCategory: item?.gaEventData?.subcategory,
        productId: item?.productId,
        productName: item?.gaEventData?.productName,
        productUse: item?.gaEventData?.productUse,
        qty: item?.quantity,
        skuId: item?.catalogRefId,
      })),
      orderFulfillmentType: orderData?.orderShipType,
      orderID: orderData?.id,
      paymentMethod: orderData?.paymentGroups?.map(
        (pg: any) => pg?.paymentGroupClassType,
      ),
      pickupOptIn: isBopisOrder && emailInfo?.phoneNumber ? true : false,
      ropisOnlySubtotal: orderData?.inStorePickupItemsSubTotal,
      shipOnlySubtotal: orderData?.webOnlyItemsSubTotal,
      shipping: orderData?.priceInfo?.shipping,
      storeSelected: isBopisOrder
        ? `${storeDetails?.name}, ${storeDetails?.stateAddress}`
        : '',
      subtotal: orderData?.priceInfo?.rawSubtotal,
      success: true,
      tax: orderData?.priceInfo?.tax,
      total: orderData?.priceInfo?.total,
      userShopFor: emailInfo?.userShopFor,
      taxExempt: isTaxExemptOpted,
    };
    setInSessionStorage('llSubmitOrderDTMData', eventData);
    return;
  };

  /**
   * Handles successful PayPal payment
   */
  const handleSuccessPayment = async (): Promise<void> => {
    setLoading(true);
    try {
      await fetchCheckoutDetails({ dynSessConf, dispatch, isFurniture });
    } catch (error) {
      console.error(
        'Failed to fetch checkout details after PayPal payment:',
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  // Determine if the submit button should be disabled
  const isSubmitDisabled =
    !isSubmitOrder ||
    (isRegistryOrder || isCTLOrder
      ? !isGiftItemAnonymous && giftingItemFrom === ''
        ? true
        : false
      : false);

  return (
    <Conditional if={paymentStep}>
      <>
        {/* Regular Submit Button (for non-PayPal or authorized PayPal) */}
        <Conditional
          if={
            paymentMethod !== PAYMENT.PAYPAL_OPTION ||
            (paymentMethod === PAYMENT.PAYPAL_OPTION && isPayPalAuthorized) ||
            isGooglePayAuthorized
          }
        >
          <div className="d-flex justify-content-end mt-ll-5">
            <LLButton
              customClass="col-12 col-md-auto"
              variation={'secondary'}
              disabled={isSubmitDisabled || loading}
              onClick={handleOrderSubmit}
              aria-label="Submit Order"
              data-testid="submit-order-button"
            >
              Submit Order
            </LLButton>
          </div>
        </Conditional>

        {/* PayPal Button (when PayPal is selected but not yet authorized) */}
        <Conditional
          if={paymentMethod === PAYMENT.PAYPAL_OPTION && !isPayPalAuthorized}
        >
          <div className="col mt-ll-5 d-flex ms-auto col-sm-4">
            <PayPalButton
              isCheckout={true}
              openRegistryModal={undefined}
              showRegistryAddressModal={false}
              showClearTheListAddressModal={false}
              showCustomlogo={false}
              height={44}
              buttonColor={`gold`}
              customClass={`${styles['paypal-button-wrapper']} ${isFurniture ? styles['f-style'] : ''}`}
              handleSuccessPayment={handleSuccessPayment}
            />
          </div>
        </Conditional>

        {/* Loading Indicator */}
        <Conditional if={loading}>
          <Loader aria-label="Processing your order" />
        </Conditional>
      </>
    </Conditional>
  );
};

export default SubmitOrder;
